import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ArrowBack as ArrowBackIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface ChequeCartera {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroMovCajaIngre: number | null;
  nroDestino: number;
  fechaSalida: string | null;
  nroMovCajaSalida: number | null;
  fechaVencimiento: string;
  concepto: string;
  fechaEmision: string;
  conciliado: number;
  asentado: number;
  mcp: string;
  nroCuenta: string;
}

const ChequesTercerosCartera: React.FC = () => {
  const navigate = useNavigate();
  const [cheques, setCheques] = useState<ChequeCartera[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params: any = {
          pageNumber: page + 1,
          pageSize: Math.min(rowsPerPage, 20) // Máximo 20
        };

        if (searchTerm.trim()) {
          params.searchTerm = searchTerm.trim();
        }

        const response = await api.get('/api/Tesoreria/cheques-cartera', {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
          params
        });

        const data = response.data;
        let chequesArray: ChequeCartera[] = [];
        let count = 0;
        
        if (data?.chequesCartera && Array.isArray(data.chequesCartera)) {
          chequesArray = data.chequesCartera;
          count = data.totalCount || data.chequesCartera.length;
        } else if (data?.items && Array.isArray(data.items)) {
          chequesArray = data.items;
          count = data.totalCount || data.items.length;
        } else if (Array.isArray(data)) {
          chequesArray = data;
          count = data.length;
        }
        
        setCheques(chequesArray);
        setTotalCount(count);
      } catch (error: any) {
        console.error('Error al obtener los cheques de terceros en cartera:', error);
        enqueueSnackbar(
          error.response?.data?.message || 'No se pudieron cargar los cheques de terceros en cartera.',
          { variant: 'error' }
        );
        setCheques([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() ? 500 : 0);

    return () => clearTimeout(timer);
  }, [searchTerm, page, rowsPerPage, enqueueSnackbar]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Math.min(parseInt(event.target.value, 10), 20);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tesoreria/informes')}
              sx={{ color: '#1976d2' }}
            >
              Volver
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', flexGrow: 1 }}>
              Cheques de Terceros en Cartera
            </Typography>
          </Stack>

          {/* Búsqueda */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por librador, banco, número de cheque..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 500 }}
            />
          </Box>

          {/* Tabla */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Carga</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Cheque</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Banco</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Librador</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Importe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Emisión</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Vencimiento</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Ingreso</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Salida</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cheques.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {searchTerm ? 'No se encontraron cheques con los criterios de búsqueda' : 'No hay cheques de terceros en cartera'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cheques.map((cheque, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{cheque.nroCarga || '-'}</TableCell>
                          <TableCell>{cheque.nroCheque || '-'}</TableCell>
                          <TableCell>{cheque.nombreBanco?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.nombreSucursal?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.librador?.trim() || cheque.nroLibrador?.trim() || '-'}</TableCell>
                          <TableCell>{formatCurrency(cheque.importe || 0)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaEmision)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaVencimiento)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaIngreso)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaSalida || '')}</TableCell>
                          <TableCell>{cheque.concepto?.trim() || '-'}</TableCell>
                          <TableCell>
                            {cheque.conciliado ? 'Conciliado' : cheque.fechaSalida ? 'Egresado' : 'En cartera'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`}
                />
              </>
            )}
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChequesTercerosCartera;

